import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { autoPrefixUS, isValidE164 } from "../components/PhoneInput";
import {
  IdentificationCard,
  Car,
  ShieldCheck,
  House,
  Signature,
  CheckCircle,
  CaretRight,
  CaretLeft,
  Warning,
  Confetti,
} from "@phosphor-icons/react";
import { BACKEND_URL } from "../lib/api";
import PageBackLink from "../components/PageBackLink";

const STEPS = [
  { key: "identity", label: "Identity",    Icon: IdentificationCard },
  { key: "license",  label: "License",     Icon: IdentificationCard },
  { key: "vehicle",  label: "Vehicle",     Icon: Car },
  { key: "address",  label: "Address",     Icon: House },
  { key: "consent",  label: "Consents",    Icon: ShieldCheck },
  { key: "review",   label: "Review",      Icon: Signature },
];

const inputCls =
  "w-full rounded-md border border-[#E2E0D8] bg-white px-3 py-2.5 text-sm text-slate_navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brandblue/20 focus:border-brandblue transition";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] font-bold text-slate_navy mb-1.5";

export default function DriverOnboard() {
  const { inviteCode } = useParams();
  const [driver, setDriver] = useState(null);
  const [loadingLookup, setLoadingLookup] = useState(true);
  const [lookupError, setLookupError] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    legal_name: "",
    date_of_birth: "",
    ssn_last4: "",
    phone: "",
    license_number: "",
    license_state: "",
    license_class: "",
    license_expires_on: "",
    license_endorsements: [],
    vehicle_type: "sedan",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    vehicle_plate: "",
    vehicle_vin: "",
    address_line1: "",
    address_city: "",
    address_state: "",
    address_postal_code: "",
    consent_mvr_pull: false,
    consent_background_check: false,
    consent_drug_screen: false,
    consent_sms: false,
    baa_signature_text: "",
  });

  useEffect(() => {
    let mounted = true;
    axios.get(`${BACKEND_URL}/api/driver/onboard/${inviteCode}`)
      .then(({ data }) => {
        if (!mounted) return;
        setDriver(data);
        setForm((f) => ({
          ...f,
          legal_name: data.name || "",
          phone: data.phone || "",
          vehicle_type: data.vehicle_type || "sedan",
        }));
      })
      .catch((e) => {
        if (!mounted) return;
        setLookupError(e?.response?.data?.detail || "This invite link is invalid or expired.");
      })
      .finally(() => mounted && setLoadingLookup(false));
    return () => { mounted = false; };
  }, [inviteCode]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    const f = form;
    if (step === 0) return f.legal_name.trim().length >= 2 && isValidE164(autoPrefixUS(f.phone)) && (!f.ssn_last4 || /^\d{4}$/.test(f.ssn_last4));
    if (step === 3) return f.address_line1 && f.address_city && f.address_state && f.address_postal_code;
    if (step === 4) return f.consent_mvr_pull && f.consent_background_check && f.consent_drug_screen && f.consent_sms;
    if (step === 5) return f.baa_signature_text.trim().length >= 3;
    return true;
  };

  const next = () => {
    if (!validateStep()) {
      toast.error(step === 0 ? "Enter your full name and a 10-digit US mobile number." : "Please complete the required fields on this step.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validateStep()) {
      toast.error("Type your full name in the signature box to certify.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, phone: autoPrefixUS(form.phone) };
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      payload.vehicle_year = form.vehicle_year ? parseInt(form.vehicle_year, 10) : null;
      await axios.post(`${BACKEND_URL}/api/driver/onboard/${inviteCode}`, payload);
      setDone(true);
      toast.success("Onboarding submitted — credentialing review starting.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed — please double-check every field.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingLookup) {
    return <Shell><Card><p className="text-sm text-slate_navy">Loading your invite…</p></Card></Shell>;
  }
  if (lookupError) {
    return (
      <Shell>
        <Card tone="error">
          <Warning size={36} weight="fill" className="text-rose-500 mx-auto" />
          <h1 className="mt-3 font-heading text-2xl font-black text-ink">Invite link not valid</h1>
          <p className="mt-2 text-sm text-slate-600">{lookupError}</p>
          <p className="mt-4 text-xs text-slate-500">Call dispatch at (833) 659-2699 and we&apos;ll send a fresh invite.</p>
        </Card>
      </Shell>
    );
  }
  if (done) {
    return (
      <Shell>
        <Card tone="ok">
          <Confetti size={42} weight="fill" className="text-forest mx-auto" />
          <h1 className="mt-3 font-heading text-3xl font-black text-ink">You&apos;re submitted, {driver.name?.split(" ")[0]}</h1>
          <p className="mt-3 text-sm text-slate_navy leading-relaxed">
            Your application is now in MedexOva&apos;s credentialing exchange. You&apos;ll receive an email when:
          </p>
          <ul className="mt-3 space-y-2 text-sm text-slate_navy">
            <li className="flex items-start gap-2"><CheckCircle size={16} className="mt-0.5 text-forest shrink-0" /> Background check, MVR, and drug screen complete</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} className="mt-0.5 text-forest shrink-0" /> Your Medex Group insurance policy is active</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} className="mt-0.5 text-forest shrink-0" /> You&apos;re cleared to start picking up trips</li>
          </ul>
          <p className="mt-5 text-xs text-slate-500">
            In the meantime, please install the <b>OVA Driver app</b> on your phone — that&apos;s where you&apos;ll see and accept trips once you&apos;re cleared. Link will be in your clearance email.
          </p>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="max-w-2xl mx-auto" data-testid="driver-onboard">
        {/* Step rail */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 mb-6">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`flex items-center gap-1.5 shrink-0 ${i < STEPS.length - 1 ? "pr-2" : ""}`}>
              <div
                data-testid={`step-pill-${s.key}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-bold transition ${
                  i === step
                    ? "bg-brandblue text-white border-brandblue"
                    : i < step
                      ? "bg-forest text-white border-forest"
                      : "bg-white text-slate-500 border-[#E2E0D8]"
                }`}
              >
                <s.Icon size={12} weight={i <= step ? "fill" : "regular"} />
                <span>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className="h-px w-3 bg-[#E2E0D8]" />}
            </div>
          ))}
        </div>

        <Card>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue">
            Step {step + 1} of {STEPS.length} · {STEPS[step].label}
          </div>

          {step === 0 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="step-identity">
              <Field label="Legal full name *" full>
                <input value={form.legal_name} onChange={(e) => update("legal_name", e.target.value)} className={inputCls} placeholder="Jane Operator" data-testid="legal-name-input" />
              </Field>
              <Field label="Date of birth (optional)">
                <input type="date" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} className={inputCls} data-testid="dob-input" />
              </Field>
              <Field label="Last 4 of SSN (optional)">
                <input value={form.ssn_last4} maxLength={4} onChange={(e) => update("ssn_last4", e.target.value.replace(/\D/g, ""))} className={inputCls} placeholder="4321" data-testid="ssn-input" />
              </Field>
              <Field label="Mobile phone *" full>
                <input value={form.phone} onChange={(e) => update("phone", e.target.value)}
                       onBlur={() => update("phone", autoPrefixUS(form.phone))}
                       className={inputCls} placeholder="(940) 555-0100" data-testid="phone-input" />
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="step-license">
              <p className="sm:col-span-2 text-xs text-slate-500">Optional for now — skip anything you don&apos;t have handy. Our team will help you finish it.</p>
              <Field label="License number">
                <input value={form.license_number} onChange={(e) => update("license_number", e.target.value)} className={inputCls} placeholder="12345678" />
              </Field>
              <Field label="State">
                <input value={form.license_state} maxLength={2} onChange={(e) => update("license_state", e.target.value.toUpperCase())} className={inputCls} placeholder="TX" />
              </Field>
              <Field label="Class">
                <input value={form.license_class} maxLength={4} onChange={(e) => update("license_class", e.target.value.toUpperCase())} className={inputCls} placeholder="C" />
              </Field>
              <Field label="Expires on">
                <input type="date" value={form.license_expires_on} onChange={(e) => update("license_expires_on", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Endorsements (optional)" full>
                <input value={form.license_endorsements.join(", ")}
                  onChange={(e) => update("license_endorsements", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                  className={inputCls} placeholder="P, T, S — comma separated" />
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="step-vehicle">
              <p className="sm:col-span-2 text-xs text-slate-500">Optional for now — skip anything you don&apos;t have handy.</p>
              <Field label="Vehicle type" full>
                <select value={form.vehicle_type} onChange={(e) => update("vehicle_type", e.target.value)} className={inputCls}>
                  <option value="sedan">Sedan</option>
                  <option value="suv">SUV</option>
                  <option value="wheelchair">Wheelchair-accessible van</option>
                  <option value="stretcher">Stretcher-equipped</option>
                </select>
              </Field>
              <Field label="Make">
                <input value={form.vehicle_make} onChange={(e) => update("vehicle_make", e.target.value)} className={inputCls} placeholder="Ford" />
              </Field>
              <Field label="Model">
                <input value={form.vehicle_model} onChange={(e) => update("vehicle_model", e.target.value)} className={inputCls} placeholder="Transit" />
              </Field>
              <Field label="Year">
                <input type="number" min="1990" max="2099" value={form.vehicle_year} onChange={(e) => update("vehicle_year", e.target.value)} className={inputCls} placeholder="2023" />
              </Field>
              <Field label="License plate">
                <input value={form.vehicle_plate} onChange={(e) => update("vehicle_plate", e.target.value.toUpperCase())} className={inputCls} placeholder="TX-ABC-1234" />
              </Field>
              <Field label="VIN" full>
                <input value={form.vehicle_vin} onChange={(e) => update("vehicle_vin", e.target.value.toUpperCase())} className={inputCls} placeholder="1FTBR2X8XPKA12345" />
              </Field>
            </div>
          )}

          {step === 3 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="step-address">
              <Field label="Street address *" full>
                <input value={form.address_line1} onChange={(e) => update("address_line1", e.target.value)} className={inputCls} placeholder="123 Main St" data-testid="address-line1-input" />
              </Field>
              <Field label="City *">
                <input value={form.address_city} onChange={(e) => update("address_city", e.target.value)} className={inputCls} placeholder="Plano" data-testid="address-city-input" />
              </Field>
              <Field label="State *">
                <input value={form.address_state} maxLength={2} onChange={(e) => update("address_state", e.target.value.toUpperCase())} className={inputCls} placeholder="TX" data-testid="address-state-input" />
              </Field>
              <Field label="Postal code *" full>
                <input value={form.address_postal_code} onChange={(e) => update("address_postal_code", e.target.value)} className={inputCls} placeholder="75024" data-testid="address-postal-input" />
              </Field>
            </div>
          )}

          {step === 4 && (
            <div className="mt-4 space-y-3 text-sm text-slate_navy" data-testid="step-consent">
              <p className="text-xs text-slate-500">
                To complete credentialing, MedexOva needs your consent to pull these reports. Each is required by NEMT regulations + the Medex Group captive insurance program.
              </p>
              {[
                { k: "consent_mvr_pull",         label: "Motor Vehicle Report (MVR)",     desc: "I authorize MedexOVA and MedexOva to pull my 3-year driving record." },
                { k: "consent_background_check", label: "Criminal background check",      desc: "I authorize a county + federal criminal background check." },
                { k: "consent_drug_screen",      label: "5-panel drug screen",             desc: "I agree to complete a 5-panel pre-employment drug screen at a partnered lab within 7 days." },
              ].map(({ k, label, desc }) => (
                <label key={k} className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E0D8] bg-white hover:border-brandblue/40 cursor-pointer transition">
                  <input
                    type="checkbox"
                    checked={form[k]}
                    onChange={(e) => update(k, e.target.checked)}
                    data-testid={`consent-${k}`}
                    className="mt-1 h-4 w-4 accent-brandblue"
                  />
                  <span>
                    <span className="block font-semibold">{label}</span>
                    <span className="block text-xs text-slate-500 mt-0.5">{desc}</span>
                  </span>
                </label>
              ))}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E0D8] bg-[#F9F8F6] hover:border-brandblue/40 cursor-pointer transition" data-testid="consent-sms-wrap">
                <input
                  type="checkbox"
                  checked={form.consent_sms}
                  onChange={(e) => update("consent_sms", e.target.checked)}
                  data-testid="consent-consent_sms"
                  className="mt-1 h-4 w-4 accent-brandblue"
                />
                <span>
                  <span className="block font-semibold">SMS &amp; voice dispatch messages</span>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    I agree to receive automated SMS and voice messages from MedexOVA — trip offers, dispatch
                    updates, and your Beacon link. Message frequency varies by dispatch activity. Msg &amp; data
                    rates may apply. Reply STOP to cancel, HELP for help. See our{" "}
                    <a href="/sms-terms" target="_blank" rel="noreferrer" className="underline font-semibold">SMS Terms</a>.
                  </span>
                </span>
              </label>
            </div>
          )}

          {step === 5 && (
            <div className="mt-4 space-y-4" data-testid="step-review">
              <div className="rounded-lg border border-[#E2E0D8] bg-slate-50 p-4 text-xs text-slate_navy max-h-48 overflow-y-auto leading-relaxed">
                <p className="font-bold uppercase tracking-[0.2em] text-brandblue mb-2">HIPAA Business Associate Agreement (summary)</p>
                <p>
                  By signing below, I agree to safeguard all protected health information (PHI) I encounter as
                  an MedexOVA driver. I will not disclose patient information to anyone outside the dispatch chain.
                  I will report any suspected breach to MedexOVA within 24 hours. This binds me under the HIPAA
                  Privacy and Security Rules and the Medex Group BAA.
                </p>
                <p className="mt-2">
                  I also authorize MedexOVA to share my submitted information with MedexOva Credentialing Exchange
                  and the Medex Group Insurance Captive for the purpose of credentialing and enrollment.
                </p>
              </div>
              <Field label="Type your full legal name to sign">
                <input
                  value={form.baa_signature_text}
                  onChange={(e) => update("baa_signature_text", e.target.value)}
                  className={inputCls}
                  placeholder={form.legal_name || "Jane Operator"}
                  data-testid="baa-signature-input"
                />
              </Field>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={back}
              disabled={step === 0 || submitting}
              data-testid="wizard-back-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E0D8] bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate_navy px-4 py-2 text-xs font-semibold uppercase tracking-wider transition"
            >
              <CaretLeft size={12} weight="bold" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={next}
                data-testid="wizard-next-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-brandblue hover:bg-brandblue-hover text-white px-5 py-2 text-xs font-bold uppercase tracking-wider transition"
              >
                Continue <CaretRight size={12} weight="bold" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="wizard-submit-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-forest hover:bg-forest-hover disabled:opacity-60 text-white px-5 py-2 text-xs font-bold uppercase tracking-wider transition"
              >
                {submitting ? "Submitting…" : "Submit for credentialing"}
              </button>
            )}
          </div>
        </Card>

        <p className="mt-4 text-center text-[11px] text-slate-500">
          Need help? Call dispatch at (833) 659-2699 — we&apos;re happy to walk through this with you.
        </p>
      </div>
    </Shell>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

function Shell({ children }) {
  return (
    <main className="min-h-screen bg-bone px-4 py-10">
      <PageBackLink fallbackTo="/" label="Back to home" style={{ padding: "0 0 16px" }} />
      <div className="max-w-2xl mx-auto mb-6 text-center">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brandblue">MedexOVA driver onboarding</div>
        <h1 className="mt-1 font-heading text-3xl font-black text-ink">Welcome to the driver network</h1>
      </div>
      {children}
    </main>
  );
}

function Card({ children, tone }) {
  const toneCls = tone === "error" ? "border-rose-300 bg-rose-50/60"
                : tone === "ok" ? "border-forest/30 bg-forest/5"
                : "border-[#E2E0D8] bg-white";
  return (
    <div className={`max-w-2xl mx-auto rounded-2xl border ${toneCls} p-6 sm:p-8 text-center sm:text-left`}>
      {children}
    </div>
  );
}
